import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-ink/25 px-4 py-3 font-body text-lg text-ink focus:border-ink outline-none";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/my-list");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <p className="mono-label mb-4">Sign in</p>
      <h1 className="font-heading text-4xl text-ink leading-none">Return to the circle</h1>
      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
        <label className="block"><span className="mono-label block mb-2">Email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email" /></label>
        <label className="block"><span className="mono-label block mb-2">Password</span>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password" /></label>
        {error && <p className="font-body text-terracotta" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-solid w-full" data-testid="login-submit">{busy ? "Attending…" : "Sign in"}</button>
      </form>
      <div className="mt-8 flex justify-between mono-label">
        <Link to="/register" className="underline underline-offset-4">Create an account</Link>
        <Link to="/forgot-password" className="underline underline-offset-4">Forgot password</Link>
      </div>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await register(f);
      navigate("/vigil");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <p className="mono-label mb-4">Join the circle</p>
      <h1 className="font-heading text-4xl text-ink leading-none">Create an account</h1>
      <p className="font-body text-ink-secondary mt-4 leading-relaxed">An account lets you hold prayers, keep a private journal, and receive the Monday Vigil. You may submit a prayer without one.</p>
      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="register-form">
        <label className="block"><span className="mono-label block mb-2">Name</span>
          <input required value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="register-name" /></label>
        <label className="block"><span className="mono-label block mb-2">Email</span>
          <input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="register-email" /></label>
        <label className="block"><span className="mono-label block mb-2">Password</span>
          <input type="password" required minLength={8} value={f.password} onChange={(e) => set("password", e.target.value)} className={inputCls} data-testid="register-password" />
          <span className="font-body text-sm text-ink-tertiary mt-1 block italic">At least 8 characters.</span></label>
        {error && <p className="font-body text-terracotta" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-solid w-full" data-testid="register-submit">{busy ? "Attending…" : "Join"}</button>
      </form>
      <Link to="/login" className="mono-label underline underline-offset-4 mt-8 inline-block">Already have an account</Link>
    </div>
  );
}
